/**
 * Country search for every cmdk country picker (ob-09 U5, U6).
 *
 * cmdk's default scorer is fuzzy: it matches letters scattered anywhere in the
 * name, so "tur" listed Portugal, Mauritius and Mauritania. A country search
 * should match what people type: the start of the name, the start of any
 * word, the exact ISO code, an alias, or a calling code.
 */

/** Other names a customer may type for a country. The old exonym stays findable (U5). */
export const COUNTRY_SEARCH_ALIASES: Readonly<Record<string, readonly string[]>> = {
  TR: ['Turkey', 'Turkiye'],
}

/** Lowercase, trimmed, without diacritics: "Türkiye" and "turkiye" compare equal. */
export function foldForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

/** The keywords a country's cmdk item carries besides its label: its ISO code and aliases. */
export function countrySearchKeywords(code: string): string[] {
  return [code, ...(COUNTRY_SEARCH_ALIASES[code] ?? [])]
}

const WORD_BREAK = /[\s\-+(),.'’/]+/

const ISO_CODE = /^[A-Z]{2}$/

/**
 * cmdk `filter`: 1 for an exact name or an empty search, 0.9 for a prefix of
 * the whole name, 0.8 for an exact ISO code, 0.7 for a prefix of any word in a
 * name, 0.5 for a prefix of an ISO code, 0 otherwise (hidden). A name prefix
 * outranks an exact code, so "de" lists Denmark before Germany (DE).
 * `value` is the item's value (a label, or an ISO code in the phone picker);
 * `keywords` are the item's other names.
 */
export function countrySearchScore(value: string, search: string, keywords: string[] = []): number {
  const query = foldForSearch(search)
  if (!query) return 1
  let best = 0
  for (const candidate of [value, ...keywords]) {
    const text = foldForSearch(candidate)
    if (!text) continue
    // Checked on the raw candidate, before folding: only an ISO code is two
    // capital letters, and folding would make it look like any short name.
    if (ISO_CODE.test(candidate)) {
      if (text === query) best = Math.max(best, 0.8)
      else if (text.startsWith(query)) best = Math.max(best, 0.5)
      continue
    }
    if (text === query) return 1
    if (text.startsWith(query)) {
      best = Math.max(best, 0.9)
    } else if (text.split(WORD_BREAK).some((word) => word.length > 0 && word.startsWith(query))) {
      best = Math.max(best, 0.7)
    }
  }
  return best
}
